import {
  type ChartOptions,
  CrosshairMode,
  createChart,
  type DeepPartial,
  type IChartApi,
  type ISeriesApi,
  LineStyle,
  type SeriesType,
} from "lightweight-charts";
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { CHART_COLOR_PARSERS, readFontStack, readPalette } from "./chartPalette.js";

const disposedCharts = new WeakSet<IChartApi>();

/**
 * True once this chart has been torn down.
 *
 * Pane cleanups check it before touching series or subscriptions, because
 * `useChart`'s own cleanup calls `chart.remove()` and, on unmount, that runs
 * before the passive cleanups of every effect built on top of it.
 */
export function isChartDisposed(chart: IChartApi | null): boolean {
  return chart !== null && disposedCharts.has(chart);
}

/**
 * Record a chart as torn down. Call it immediately before `chart.remove()`.
 *
 * `useChart` does this for the charts it owns. A component that calls
 * `createChart` itself — `signal-detail/FocusChart.tsx` — has the same
 * cleanup-ordering problem and must register here too, or every
 * `isChartDisposed` guard downstream of it silently reads `false`.
 */
export function markChartDisposed(chart: IChartApi): void {
  disposedCharts.add(chart);
}

/**
 * Remove a series without throwing if the chart is already gone.
 *
 * `chart.removeSeries()` on a destroyed chart throws "Value is undefined" from
 * the library's internal `ensureDefined`. This is the ordinary unmount path,
 * not an edge case — see `isChartDisposed`.
 */
export function removeSeriesSafe(
  chart: IChartApi | null,
  series: ISeriesApi<SeriesType> | null,
): void {
  // The disposal check comes first, and swallowing the throw is not a
  // substitute for it: `removeSeries` invalidates the model on its way to
  // failing, which schedules a draw the destroyed chart already cancelled —
  // and that frame then paints against a disposed canvas binding.
  if (!chart || !series || isChartDisposed(chart)) return;
  try {
    chart.removeSeries(series);
  } catch {
    // Raced a disposal between the check and the call.
  }
}

/**
 * Owns one lightweight-charts instance's lifetime and keeps it sized to its
 * container.
 *
 *   const ref = useRef<HTMLDivElement>(null);
 *   const chart = useChart(ref);
 *   return <div ref={ref} style={{ height: PRICE_PANE_HEIGHT }} />;
 */
export function useChart(
  containerRef: RefObject<HTMLDivElement | null>,
  extraOptions?: DeepPartial<ChartOptions>,
): IChartApi | null {
  const [chart, setChart] = useState<IChartApi | null>(null);
  // Latest options in a ref, so the lifetime effect does not depend on them.
  // Recreating the chart on a prop change would drop every series a caller
  // has attached to it.
  const optsRef = useRef(extraOptions);
  optsRef.current = extraOptions;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const palette = readPalette();
    const extra = optsRef.current ?? {};
    const instance = createChart(el, {
      layout: {
        background: { color: "transparent" },
        textColor: palette.muted,
        fontFamily: readFontStack(),
        fontSize: 11,
        // Set at creation only — applying a different parser list later throws.
        colorParsers: [...CHART_COLOR_PARSERS],
      },
      grid: {
        vertLines: { color: palette.grid, style: LineStyle.Dotted },
        horzLines: { color: palette.grid, style: LineStyle.Dotted },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: palette.muted, width: 1, labelBackgroundColor: palette.border },
        horzLine: { color: palette.muted, width: 1, labelBackgroundColor: palette.border },
      },
      handleScroll: true,
      handleScale: true,
      autoSize: false,
      ...extra,
      // Rebuilt AFTER the spread and merged key by key, deliberately. A plain
      // spread replaces the whole sub-object, so a caller passing its own
      // `timeScale` — which every axis-hiding pane does — silently drops
      // `rightOffset: 4`. That pane then sits at the library default of 0 while
      // the main pane sits at 4, and `useTimeScaleSync`'s one-shot range push
      // clamps to it: four bars of permanent offset. Pinned in the test.
      rightPriceScale: { borderColor: palette.border, ...extra.rightPriceScale },
      timeScale: {
        borderColor: palette.border,
        rightOffset: 4,
        barSpacing: 6,
        ...extra.timeScale,
      },
    });

    const rect = el.getBoundingClientRect();
    instance.applyOptions({
      width: Math.max(1, Math.floor(rect.width)),
      height: Math.max(1, Math.floor(rect.height)),
    });
    setChart(instance);

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      instance.applyOptions({
        width: Math.max(1, Math.floor(entry.contentRect.width)),
        height: Math.max(1, Math.floor(entry.contentRect.height)),
      });
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      markChartDisposed(instance);
      instance.remove();
      setChart(null);
    };
  }, [containerRef]);

  // Theme-only option changes, without rebuilding the chart.
  useEffect(() => {
    if (!chart || !extraOptions) return;
    chart.applyOptions(extraOptions);
  }, [chart, extraOptions]);

  return chart;
}
